/**
 * Stable leading phrase every page-context preamble begins with. Used both to
 * build the preamble and to strip it back off persisted messages on history
 * reload, so the two never drift.
 */
const PAGE_CONTEXT_PREAMBLE_PREFIX = 'The user is currently viewing the published Flora page';

/**
 * Fixed closing sentence of every page-context preamble. Paired with the
 * separator it forms the exact envelope boundary the stripper cuts at, so the
 * cut lands past the whole preamble rather than at the first blank line inside
 * it, and a user message that merely happens to open with the prefix is left
 * alone unless it also reproduces this closing sentence verbatim.
 *
 * Every already-persisted preamble ends with this sentence, so anchoring on it
 * strips historical messages identically — but the wording is now load-bearing
 * for stored data. Changing it needs a migration, not a test update.
 */
const PAGE_CONTEXT_PREAMBLE_SUFFIX = '. Use this as context for their questions.';

/** Separator between the injected preamble and the user's typed text. */
const PAGE_CONTEXT_PREAMBLE_SEPARATOR = '\n\n';

/**
 * The full generated envelope boundary: the preamble's closing sentence
 * immediately followed by the separator. `indexOf` on this literal is what
 * makes stripping independent of anything the page title contains — the
 * builder flattens the assembled sentence, so the title cannot forge it.
 */
const PAGE_CONTEXT_PREAMBLE_BOUNDARY = `${PAGE_CONTEXT_PREAMBLE_SUFFIX}${PAGE_CONTEXT_PREAMBLE_SEPARATOR}`;

/**
 * How far back the envelope's id parenthetical is looked for. Generous next to
 * a real id (a UUID is 36 characters) and bounded so the scan stays linear.
 */
const ENVELOPE_ID_LOOKBACK = 320;

/** Matches `(page_id: …)` at the end of the text preceding a boundary. */
const PAGE_ID_ANCHOR = /\(page_id: [^()]*\)$/;

/**
 * Collapses every run of whitespace — newlines included — down to a single
 * space. Applied to a whole assembled context preamble, because the fields
 * interpolated into one are free text at the wire boundary: a page title is
 * backend- or LLM-authored and typed only as `string`. An embedded blank line
 * would otherwise reproduce the separator inside the envelope and forge the
 * boundary the stripper looks for.
 *
 * @param {string} value - The assembled preamble sentence, possibly multi-line.
 * @returns {string} The value as a single line, with no leading/trailing space.
 */
function flattenToSingleLine(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Builds the context preamble that seeds Flora with the page currently on
 * screen, so a chat opened from the page viewer knows which page is being
 * viewed. The raw `pageId` is included as a stable reference the agent can act
 * on; the title is included when known.
 *
 * @param {string} pageId - The published page's id (always included).
 * @param {string} [title] - The page title, when available.
 * @returns {string} The single-line preamble sentence (no trailing separator).
 */
function buildPageContextPreamble(pageId: string, title?: string): string {
  return flattenToSingleLine(
    `${PAGE_CONTEXT_PREAMBLE_PREFIX}${title ? ` "${title}"` : ''} (page_id: ${pageId})${PAGE_CONTEXT_PREAMBLE_SUFFIX}`,
  );
}

/**
 * Prepends `preamble` to `message` for the wire, keeping the recoverable
 * separator so the preamble can be stripped back off on history reload.
 *
 * @param {string} preamble - The context preamble from `buildPageContextPreamble`.
 * @param {string} message - The user's typed message.
 * @returns {string} The combined message sent to the agent.
 */
function applyPageContextPreamble(preamble: string, message: string): string {
  return `${preamble}${PAGE_CONTEXT_PREAMBLE_SEPARATOR}${message}`;
}

/**
 * Removes a generated context envelope from the front of a persisted message.
 *
 * Shared by both preamble modules so the two can never drift apart on the one
 * decision that matters: WHICH occurrence of the boundary is the real one.
 *
 * A boundary is only accepted when the text immediately before it is the id
 * parenthetical — `(page_id: …)` or `(session_id: …)` — because every generated
 * preamble puts that directly before the closing sentence. That distinction
 * matters for LEGACY records, written before the builders flattened their
 * input: one of those can carry a free-text title holding the entire boundary,
 * and cutting at the first match would return the tail of the preamble instead
 * of the user's message. Scanning for the first correctly-anchored match steps
 * over the forged one and still finds the real one — while a legacy title that
 * merely contains a blank line keeps stripping correctly, which a "must be
 * single-line" check would have broken.
 *
 * Matching the full parenthetical rather than a bare `)` matters: a legacy
 * title ending in an ordinary parenthetical (`"Q3 (2024)"`) followed by a
 * forged boundary would satisfy a bare-`)` test and cut early. Neither
 * first-match nor last-match dominates the other on their own — anchoring on
 * the label is what makes the choice safe rather than a trade-off.
 *
 * @param {string} content - A persisted user-message string.
 * @param {string} prefix - The stable opening phrase of the envelope.
 * @param {string} boundary - The envelope's closing sentence plus separator.
 * @param {RegExp} idAnchor - Matches the id parenthetical at the END of the text
 *   preceding a candidate boundary. Must be `$`-anchored.
 * @returns {string} The content with the leading envelope removed, or unchanged.
 */
function stripContextEnvelope(content: string, prefix: string, boundary: string, idAnchor: RegExp): string {
  if (!content.startsWith(prefix)) return content;
  let searchFrom = 0;
  for (;;) {
    const at = content.indexOf(boundary, searchFrom);
    if (at === -1) return content;
    // The cheap test first: every real boundary is preceded by the `)` that
    // closes the id parenthetical, so one character rejects almost every forged
    // candidate. The regex is `$`-anchored but not `^`-anchored, so `test`
    // would otherwise retry at each of the ~320 window positions — bounded, but
    // not the per-candidate constant this once claimed.
    if (content[at - 1] !== ')') {
      searchFrom = at + 1;
      continue;
    }
    if (idAnchor.test(content.slice(Math.max(0, at - ENVELOPE_ID_LOOKBACK), at))) {
      return content.slice(at + boundary.length);
    }
    searchFrom = at + 1;
  }
}

/**
 * Recovers the user's typed text from a persisted message that may carry an
 * injected page-context preamble. The backend stores the raw outgoing message
 * (preamble included), so history reload and the full-page handoff would
 * otherwise render the preamble in the user's first bubble. Returns the content
 * unchanged when no preamble is present, so it is safe to run over every
 * historical user message.
 *
 * Cuts at the full generated envelope — prefix at the start AND the closing
 * sentence followed by a blank line — rather than at the first blank line, so
 * a multi-line page title cannot leave preamble text behind and an ordinary
 * message that merely starts with the prefix is returned untouched.
 *
 * @param {string} content - A persisted user-message string.
 * @returns {string} The content with any leading page-context preamble removed.
 */
function stripPageContextPreamble(content: string): string {
  return stripContextEnvelope(content, PAGE_CONTEXT_PREAMBLE_PREFIX, PAGE_CONTEXT_PREAMBLE_BOUNDARY, PAGE_ID_ANCHOR);
}

export {
  applyPageContextPreamble,
  ENVELOPE_ID_LOOKBACK,
  buildPageContextPreamble,
  flattenToSingleLine,
  stripContextEnvelope,
  PAGE_CONTEXT_PREAMBLE_PREFIX,
  PAGE_CONTEXT_PREAMBLE_SEPARATOR,
  stripPageContextPreamble,
};
